import type { Admin } from '@/types';

type MemberNameSource = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

type IsCurrentUserChurchAdminParams = {
  admin: Pick<Admin, 'id' | 'userId' | 'email'>;
  currentUserId?: string;
  currentUserEmail?: string;
};

export const isCurrentUserChurchAdmin = ({
  admin,
  currentUserId,
  currentUserEmail,
}: IsCurrentUserChurchAdminParams): boolean => {
  if (!currentUserId && !currentUserEmail) {
    return false;
  }

  if (currentUserId && (admin.userId === currentUserId || admin.id === currentUserId)) {
    return true;
  }

  if (currentUserEmail && admin.email?.toLowerCase() === currentUserEmail.toLowerCase()) {
    return true;
  }

  return false;
};

export const getMemberFullName = ({ firstName, lastName }: MemberNameSource): string =>
  [firstName, lastName]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ');

export const getMemberDisplayName = ({ firstName, lastName, email }: MemberNameSource): string => {
  const fullName = getMemberFullName({ firstName, lastName });

  if (fullName) {
    return fullName;
  }

  return email?.trim() ?? '';
};

export const getMemberInitials = ({ firstName, lastName, email }: MemberNameSource): string => {
  const fullName = getMemberFullName({ firstName, lastName });

  if (fullName) {
    const nameParts = fullName.split(/\s+/);

    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
    }

    return nameParts[0].slice(0, 2).toUpperCase();
  }

  const emailValue = email?.trim() ?? '';

  if (emailValue) {
    return emailValue.charAt(0).toUpperCase();
  }

  return '';
};

export const getNoSpacingsName = (firstName: string | null, lastName: string | null) => {
  const firstInitial = firstName?.replace(/\s/g, '');
  const lastInitial = lastName?.replace(/\s/g, '');

  if (!firstInitial && !lastInitial) {
    return '';
  }

  if (!firstInitial) {
    return lastInitial ?? '';
  }

  if (!lastInitial) {
    return firstInitial;
  }

  return `${firstInitial} ${lastInitial}`;
};
